/**
 * Shippings form: create/edit shippings tied to a product–supplier link.
 * Flow: pick supplier (searchable) → pick product_supplier (products that supplier has) → (edit: pick shipping) → edit fields → submit.
 */
import React from 'react';
import { Form } from 'react-bootstrap';
import { FormModeToggle, FormAlerts, FormSubmitRow, DropdownField } from '../common';
import { useShippingsForm } from './shippingsForm/useShippingsForm';
import { useAppStore } from '../../store/appStore';

const ShippingsForm = () => {
  const insertBasicData = useAppStore((s) => s.insertBasicData);
  const isAnySectionLoading = Object.values(insertBasicData.sectionLoading || {}).some(Boolean);
  const {
    mode,
    formData,
    formError,
    formSuccess,
    submitting,
    deleting,
    selectedSupplier,
    supplierSearchQuery,
    showSupplierDropdown,
    supplierFieldTouched,
    suppliersLoading,
    filteredSuppliers,
    supplierDropdownRef,
    selectedProductSupplier,
    productSupplierSearchQuery,
    showProductSupplierDropdown,
    productSupplierFieldTouched,
    productSuppliersLoading,
    filteredProductSuppliers,
    productSupplierDropdownRef,
    selectedShipping,
    shippingSearchQuery,
    showShippingDropdown,
    shippingFieldTouched,
    shippingsLoading,
    filteredShippings,
    shippingDropdownRef,
    handleModeChange,
    handleChange,
    handleSupplierSearchChange,
    handleSelectSupplier,
    handleProductSupplierSearchChange,
    handleSelectProductSupplier,
    handleShippingSearchChange,
    handleSelectShipping,
    handleSubmit,
    handleDelete,
    isEditMode,
    setSupplierFieldTouched,
    setShowSupplierDropdown,
    setProductSupplierFieldTouched,
    setShowProductSupplierDropdown,
    setShippingFieldTouched,
    setShowShippingDropdown,
    getProductSupplierLabel,
    getShippingLabel
  } = useShippingsForm();

  const canEditFields =
    (mode === 'create' && selectedProductSupplier) || (mode === 'edit' && selectedShipping);
  const submitDisabled =
    isAnySectionLoading ||
    (mode === 'create' && !selectedProductSupplier) ||
    (isEditMode && !selectedShipping);

  return (
    <>
      <FormModeToggle mode={mode} onModeChange={handleModeChange} />
      <FormAlerts error={formError} success={formSuccess} />

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Supplier <span className="text-danger">*</span></Form.Label>
          <DropdownField
            ref={supplierDropdownRef}
            value={supplierSearchQuery}
            onChange={handleSupplierSearchChange}
            placeholder="Search by supplier name..."
            disabled={suppliersLoading || isAnySectionLoading}
            loading={suppliersLoading}
            loadingMessage="Loading suppliers..."
            showDropdown={showSupplierDropdown && !suppliersLoading && !isAnySectionLoading}
            options={filteredSuppliers}
            emptyMessage={supplierSearchQuery.trim() ? 'No matching suppliers' : 'Type to search suppliers'}
            getLabel={(s) => s.name || '(Unnamed)'}
            getItemKey={(s) => s.id}
            onSelect={handleSelectSupplier}
            onFocus={() => {
              setSupplierFieldTouched(true);
              setShowSupplierDropdown(true);
            }}
            onClick={() => setShowSupplierDropdown(true)}
            id="shipping-supplier-dropdown"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Product name <span className="text-danger">*</span></Form.Label>
          <DropdownField
            ref={productSupplierDropdownRef}
            value={productSupplierSearchQuery}
            onChange={handleProductSupplierSearchChange}
            placeholder={selectedSupplier ? 'Search by product name...' : 'Select a supplier first'}
            disabled={!selectedSupplier || productSuppliersLoading || isAnySectionLoading}
            loading={productSuppliersLoading}
            loadingMessage="Loading products..."
            showDropdown={
              showProductSupplierDropdown &&
              !!selectedSupplier &&
              !productSuppliersLoading &&
              !isAnySectionLoading
            }
            options={filteredProductSuppliers}
            emptyMessage={
              productSupplierSearchQuery.trim()
                ? 'No matching products'
                : selectedSupplier
                  ? 'No products for this supplier'
                  : 'Select a supplier first'
            }
            getLabel={getProductSupplierLabel}
            getItemKey={(ps) => ps.id}
            onSelect={handleSelectProductSupplier}
            onFocus={() => {
              setProductSupplierFieldTouched(true);
              setShowProductSupplierDropdown(true);
            }}
            onClick={() => selectedSupplier && setShowProductSupplierDropdown(true)}
            id="shipping-product-supplier-dropdown"
          />
        </Form.Group>

        {isEditMode && selectedProductSupplier && (
          <Form.Group className="mb-3">
            <Form.Label>Shipping to edit <span className="text-danger">*</span></Form.Label>
            <DropdownField
              ref={shippingDropdownRef}
              value={shippingSearchQuery}
              onChange={handleShippingSearchChange}
              placeholder="Search by carrier or country..."
              disabled={shippingsLoading || isAnySectionLoading}
              loading={shippingsLoading}
              loadingMessage="Loading shippings..."
              showDropdown={
                showShippingDropdown && !shippingsLoading && !isAnySectionLoading
              }
              options={filteredShippings}
              emptyMessage={
                shippingSearchQuery.trim()
                  ? 'No matching shippings'
                  : 'No shippings for this link'
              }
              getLabel={getShippingLabel}
              getItemKey={(s) => s.id}
              onSelect={handleSelectShipping}
              onFocus={() => {
                setShippingFieldTouched(true);
                setShowShippingDropdown(true);
              }}
              onClick={() => setShowShippingDropdown(true)}
              id="shipping-edit-dropdown"
            />
          </Form.Group>
        )}

        {canEditFields && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Ships to country</Form.Label>
              <Form.Control
                type="text"
                name="ships_to_country"
                value={formData.ships_to_country}
                onChange={handleChange}
                placeholder="e.g. Brazil"
                disabled={isAnySectionLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Ships to region</Form.Label>
              <Form.Control
                type="text"
                name="ships_to_region"
                value={formData.ships_to_region}
                onChange={handleChange}
                placeholder="e.g. Southeast"
                disabled={isAnySectionLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Lead time (days)</Form.Label>
              <Form.Control
                type="number"
                name="lead_time_days"
                step="any"
                min="0"
                value={formData.lead_time_days}
                onChange={handleChange}
                placeholder="—"
                disabled={isAnySectionLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Carrier</Form.Label>
              <Form.Control
                type="text"
                name="carrier"
                value={formData.carrier}
                onChange={handleChange}
                placeholder="e.g. UPS, FedEx"
                disabled={isAnySectionLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Freight type</Form.Label>
              <Form.Control
                type="text"
                name="freight_type"
                value={formData.freight_type}
                onChange={handleChange}
                placeholder="e.g. Standard"
                disabled={isAnySectionLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Freight price</Form.Label>
              <Form.Control
                type="number"
                name="freight_price"
                step="any"
                min="0"
                value={formData.freight_price}
                onChange={handleChange}
                placeholder="—"
                disabled={isAnySectionLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Unit of price</Form.Label>
              <Form.Control
                type="text"
                name="unit_of_price"
                value={formData.unit_of_price}
                onChange={handleChange}
                placeholder="e.g. BRL, per unit"
                disabled={isAnySectionLoading}
              />
            </Form.Group>
          </>
        )}

        <FormSubmitRow
          isEditMode={isEditMode}
          submitting={submitting}
          deleting={deleting}
          onDelete={handleDelete}
          submitLabel="Submit changes"
          deleteTitle="Delete shipping"
          disabled={submitDisabled}
          createLabel="Create shipping"
        />
      </Form>
    </>
  );
};

export default ShippingsForm;
